import {
  AccountData,
  DateAvailabilityOut,
  ExperienceData,
  OrderData,
  OrderDetailsRequestData,
  SlimEventData,
  Tip,
  VoucherOut,
} from '../../api';
import type { DateAndTimeDetails, WidgetOrderPageData } from '../core/types';
import type { PassDatedSessionSelectionDTO } from '../../api/models/PassDatedSessionSelectionDTO';
import type { PassScheduleSelectionDTO } from '../../api/models/PassScheduleSelectionDTO';
import type { ProjectSelectionLineInput } from '../../api/models/ProjectSelectionLineInput';
import { CustomTheme, SpecificEventError, VoucherError } from '../OrderUtils';
import { OrderPageIntent } from '../OrderPageIntent';

export type SpecificOrderPageConfig = {
  onlyCouples?: boolean;
  onlyVouchers?: boolean;
  standaloneGiftCard?: boolean;
  troubleshoot?: boolean;
  preSelectedDate?: string;
};
export type PersonalDetails = {
  firstname: string;
  lastname: string;
  alternative_full_name?: string;
  email: string;
  phone: string;
  recipient_email?: string;
  pass_start_date?: string | null;
};
export type OrderPageState = {
  isInitialized: boolean;
  activeStep: number;
  account: AccountData;
  experience: ExperienceData;
  orderPage: WidgetOrderPageData;
  paymentUrl: string | null;
  details: OrderDetailsRequestData;
  dateAndTimeDetails: DateAndTimeDetails;
  personalDetails: PersonalDetails;
  order_id: string;
  voucher?: VoucherOut | null;
  voucherError?: VoucherError;
  orderPageIntent: OrderPageIntent;
  theme?: CustomTheme;
  isLoadingDates: boolean;
  availableDates: DateAvailabilityOut[];
  canSpam?: boolean;
  specificOrderPage?: SpecificOrderPageConfig;
  isWalkIn?: boolean;
  returningCustomer: boolean;
  specificEvent?: SlimEventData | null;
  specificEventError?: SpecificEventError;
  tip?: Tip;
  orderOrVoucher?: OrderData | VoucherOut;
  returningCustomerFromOrderId?: string;
  savedQueryString?: string;
  passScheduleSelections: PassScheduleSelectionDTO[];
  passDatedSessionSelections: PassDatedSessionSelectionDTO[];
  passDatedSessionUnavailable?: PassDatedSessionSelectionDTO;
  projectSelection: ProjectSelectionLineInput[];
  discountedPrice?: number;
  prefilledVoucherCode?: string;
  voucherAmountUsed?: number;
  priceBeforeVoucher?: number;
  voucherBalanceLeft?: number;
};

/**
 * These three are null only until the provider finishes loading, and the provider renders a loader
 * rather than children until then - so every consumer can treat them as populated.
 */
const uninitialized = <T,>(): T => null as unknown as T;

export const orderPageInitialState: OrderPageState = {
  isInitialized: false,
  activeStep: 0,
  account: uninitialized<AccountData>(),
  experience: uninitialized<ExperienceData>(),
  orderPage: uninitialized<WidgetOrderPageData>(),
  paymentUrl: null,
  details: {
    persons: 0,
    duration: 0,
    price: 0,
    rooms: 1,
    custom_details: {},
    chosen_resources: [],
  },
  dateAndTimeDetails: {
    date: null,
    time: null,
  },
  personalDetails: {
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
  },
  order_id: '',
  voucher: undefined,
  voucherError: undefined,
  orderPageIntent: OrderPageIntent.BUY_NOW,
  theme: undefined,
  isLoadingDates: true,
  availableDates: [],
  canSpam: true,
  specificOrderPage: {
    onlyCouples: false,
    onlyVouchers: false,
    troubleshoot: false,
  },
  returningCustomer: false,
  tip: undefined,
  orderOrVoucher: undefined,
  returningCustomerFromOrderId: undefined,
  passScheduleSelections: [],
  passDatedSessionSelections: [],
  projectSelection: [],
  discountedPrice: undefined,
};
